<template>
  <div class="qaqc-test-plot-wrapper">
    <div class="qaqc-test-chart-name-top">
      <h6>QC Status Overview</h6>
      <div class="qaqc-test-header-actions">
        <div
          class="csv-btn csv-btn--plot"
          role="button"
          aria-label="Download plot as PNG"
          @click="downloadChartPng"
        >
          <img
            class="pointer csv-btn-plot-icon"
            :src="plotImageDownload"
            alt="download plot as image"
          />
        </div>
      </div>
    </div>
    <div class="qaqc-test-plots-content">
      <div ref="chartContainer" class="qaqc-test-main-chart qaqc-status-bar-chart"></div>
      <div class="qaqc-overview-chart-legend" v-if="(data.series || []).length">
        <span
          v-for="s in data.series"
          :key="s.name"
          class="qaqc-legend-item"
        >
          <span class="qaqc-legend-dot" :style="{ backgroundColor: s.color }"></span>
          {{ s.name }}
        </span>
      </div>
    </div>
  </div>
</template>

<script>
import Plotly from "plotly.js-dist-min";
import { mapGetters } from "vuex";
import { applyPlotlyGridLineStroke, mergePlotlyTheme, plotlyThemeColors } from "@/utils/chartTheme";
import { downloadPlotlyHostAsPng } from "./plotlyOverviewImageDownload.js";

export default {
  name: "QCStatusOverviewChart",
  data() {
    return {
      plotImageDownload: require("@/assets/Svg/camIcon.svg"),
    };
  },
  props: {
    data: {
      type: Object,
      default: () => ({
        categories: [],
        series: [],
      }),
    },
  },
  computed: {
    ...mapGetters("darkMode", ["isDarkMode"]),
  },
  watch: {
    "$store.state.darkMode.isDarkMode"() {
      this.$nextTick(() => this.renderChart());
    },
    data: {
      deep: true,
      handler() {
        this.$nextTick(() => this.renderChart());
      },
    },
  },
  mounted() {
    this.renderChart();
  },
  beforeUnmount() {
    if (this.$refs.chartContainer) {
      try {
        Plotly.purge(this.$refs.chartContainer);
      } catch (_) {}
    }
  },
  methods: {
    async downloadChartPng() {
      const categories = this.data.categories || [];
      const series = this.data.series || [];
      if (categories.length === 0 || series.length === 0) {
        this.$store?.commit?.("api/showSnackbar", {
          message: "No plot to export.",
          color: "#374151",
          timeout: 4000,
        });
        return;
      }
      const el = this.$refs.chartContainer;
      const theme = plotlyThemeColors(this.isDarkMode);
      const exportBg = this.isDarkMode ? theme.sceneBg : theme.plotBg;
      const ok = await downloadPlotlyHostAsPng(el, "qaqc_qc_status_overview_plot", {
        paperBgcolor: exportBg,
        plotBgcolor: exportBg,
      });
      if (!ok) {
        this.$store?.commit?.("api/showSnackbar", {
          message: "Plot image export failed.",
          color: "#B71C1C",
          timeout: 5000,
        });
      }
    },
    renderChart() {
      if (!this.$refs.chartContainer) return;
      const categories = this.data.categories || [];
      const series = this.data.series || [];
      if (categories.length === 0 || series.length === 0) return;
      const lineColor = this.isDarkMode ? "#6b7280" : "#9ca3af";
      const traces = series.map((s) => ({
        x: categories,
        y: s.values || [],
        name: s.name,
        type: "bar",
        marker: { color: s.color || "#2298D5" },
      }));
      const layout = mergePlotlyTheme(
        {
          margin: { t: 8, r: 24, b: 48, l: 56 },
          showlegend: false,
          xaxis: {
            title: "",
            tickfont: { size: 12 },
            showgrid: false,
            showline: true,
            linecolor: lineColor,
            linewidth: 1,
            zeroline: false,
          },
          yaxis: {
            title: "",
            tickfont: { size: 11 },
            rangemode: "tozero",
            showgrid: true,
            gridwidth: 1,
            griddash: "dot",
            zeroline: true,
            zerolinecolor: lineColor,
            showline: true,
            linecolor: lineColor,
            linewidth: 1,
          },
          barmode: "group",
          bargap: 0.25,
          bargroupgap: 0.05,
        },
        this.isDarkMode
      );
      const config = { responsive: true, displayModeBar: false };
      const el = this.$refs.chartContainer;
      Plotly.react(el, traces, layout, config).then(() => {
        applyPlotlyGridLineStroke(el, this.isDarkMode);
      });
    },
  },
};
</script>

<style scoped>
.qaqc-test-plot-wrapper :deep(.qaqc-test-header-actions .csv-btn--plot .csv-btn-plot-icon) {
  filter: brightness(0) invert(1);
}
</style>
